/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import { FiLink } from "react-icons/fi";
import Swal from "sweetalert2";

export default function CompanyInfoUI({
  COLORS,
  isArabic,
  isMobile,
  preview,
  isDragging,
  paymentOptions,
  form,
  loading,
  fileInputRef,
  slugInputRef,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileInputChange,
  handleRemoveImage,
  handleChange,
  handleSlugBeforeInput,
  handleSlugCompositionStart,
  handleSlugCompositionEnd,
  handleSlugBlur,
  handleSlugPaste,
  handlePaymentChange,
  handleSubmit,
  handleGoToLogin,
}) {
  return (
    <>
      {/* ✅ هذا هو الغلاف المفقود */}
      <div
        style={{
          background: COLORS.gradient,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "30px",
          fontFamily: "'Tajawal', 'Cairo', sans-serif",
          color: COLORS.textLight,
        }}
      >
        {/* ✅ الكارد الداخلي (كودك كما هو بدون أي تغيير) */}
        <div
          style={{
            maxWidth: isMobile ? "100%" : 450,
            width: "100%",
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: isMobile ? 14 : 18,
            padding: isMobile ? "25px 15px" : "40px 30px",
            boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
            backdropFilter: "blur(12px)",
            direction: isArabic ? "rtl" : "ltr",
            color: "#fff",
            margin: isMobile ? "10px" : "0",
          }}
          className="animate__animated animate__fadeInUp"
        >
          <div className="text-center mb-4">
            <h3
              className="fw-bold mb-2"
              style={{
                color: COLORS.gold,
                fontFamily: "'Cairo', sans-serif",
                fontSize: isMobile ? "1.5rem" : "1.8rem",
              }}
            >
              {isArabic ? "بيانات المتجر" : "Store Information"}
            </h3>
            <p
              style={{
                color: COLORS.textLight,
                opacity: 0.85,
                margin: 0,
                fontSize: isMobile ? "0.9rem" : "1rem",
                padding: isMobile ? "0 10px" : "0",
              }}
            >
              {isArabic
                ? "يرجى تعبئة البيانات التالية بدقة"
                : "Please fill in the following details accurately"}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-semibold text-white mb-2" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic ? "شعار المتجر" : "Store Logo"}
              </label>

              {!preview ? (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${isDragging ? COLORS.gold : "rgba(255,255,255,0.3)"}`,
                    borderRadius: 12,
                    padding: "40px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: isDragging
                      ? "rgba(242,180,65,0.1)"
                      : "rgba(255,255,255,0.05)",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDragging) {
                      e.currentTarget.style.borderColor = COLORS.gold;
                      e.currentTarget.style.background = "rgba(242,180,65,0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDragging) {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <FaCloudUploadAlt
                      size={48}
                      color={COLORS.gold}
                    />
                  </div>

                  <p style={{
                    color: COLORS.textLight,
                    margin: "8px 0",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}>
                    {isArabic
                      ? "اسحب الصورة هنا أو اضغط للاختيار"
                      : "Drag image here or click to select"}
                  </p>
                  <p style={{
                    color: COLORS.textLight,
                    opacity: 0.7,
                    fontSize: "0.85rem",
                    margin: 0,
                  }}>
                    {isArabic
                      ? "PNG, JPG, GIF حتى 5MB"
                      : "PNG, JPG, GIF up to 5MB"}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                  />
                </div>
              ) : (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <div className="text-center">
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        width: 150,
                        height: 150,
                        borderRadius: 12,
                        border: `3px solid ${COLORS.gold}`,
                        objectFit: "cover",
                        boxShadow: "0 4px 20px rgba(242,180,65,0.4)",
                      }}
                      onError={(e) => {
                        e.currentTarget.src = "https://via.placeholder.com/150";
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      style={{
                        position: "absolute",
                        top: -8,
                        [isArabic ? "left" : "right"]: -8,
                        background: "#dc2626",
                        border: "none",
                        borderRadius: "50%",
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        transition: "0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.1)";
                        e.currentTarget.style.background = "#b91c1c";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.background = "#dc2626";
                      }}
                    >
                      <FaTimes color="#fff" size={14} />
                    </button>
                  </div>
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        background: "rgba(255,255,255,0.1)",
                        border: `1px solid ${COLORS.gold}`,
                        borderRadius: 8,
                        padding: "6px 16px",
                        color: COLORS.gold,
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        transition: "0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = COLORS.gold;
                        e.currentTarget.style.color = COLORS.darkPurple;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                        e.currentTarget.style.color = COLORS.gold;
                      }}
                    >
                      {isArabic ? "تغيير الصورة" : "Change Image"}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                  />
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-white" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic ? "اسم المتجر" : "Store Name"}
              </label>
              <input
                type="text"
                name="storeName"
                onChange={handleChange}
                value={form.storeName}
                className="form-control"
                placeholder={isArabic ? "اسم المتجر" : "Store name"}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-white" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic ? "رابط المتجر (Slug)" : "Store Slug"}
              </label>
              <div
                dir="ltr"
                lang="en"
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.3)",
                  overflow: "hidden",
                  direction: "ltr",
                }}
              >
                <span
                  style={{
                    padding: isMobile ? "8px 8px 8px 12px" : "10px 10px 10px 15px",
                    color: "rgba(255,255,255,0.65)",
                    fontSize: isMobile ? "0.8rem" : "0.9rem",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  https://
                </span>
                <input
                  ref={slugInputRef}
                  type="text"
                  name="storeSlug"
                  inputMode="url"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  lang="en"
                  dir="ltr"
                  readOnly={false}
                  disabled={loading}
                  maxLength={63}
                  onChange={handleChange}
                  onBeforeInput={handleSlugBeforeInput}
                  onCompositionStart={handleSlugCompositionStart}
                  onCompositionEnd={handleSlugCompositionEnd}
                  onBlur={handleSlugBlur}
                  onPaste={handleSlugPaste}
                  value={form.storeSlug || ""}
                  placeholder="my-store"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: "transparent",
                    color: "#fff",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    padding: isMobile ? "8px 4px" : "10px 6px",
                    textAlign: "left",
                    direction: "ltr",
                    unicodeBidi: "isolate",
                    caretColor: COLORS.gold,
                    cursor: "text",
                    WebkitUserSelect: "text",
                    userSelect: "text",
                    pointerEvents: "auto",
                  }}
                />
                <span
                  style={{
                    padding: isMobile ? "8px 12px 8px 8px" : "10px 15px 10px 10px",
                    color: "rgba(255,255,255,0.65)",
                    fontSize: isMobile ? "0.8rem" : "0.9rem",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  .madar.iq
                </span>
              </div>
              <small
                className="d-block mt-2"
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: isMobile ? "0.75rem" : "0.82rem",
                  lineHeight: 1.6,
                }}
              >
                {isArabic
                  ? "أحرف إنجليزية صغيرة وأرقام وشرطة فقط — مثال: my-store"
                  : "Lowercase English letters, numbers, and hyphens only — e.g. my-store"}
              </small>
              {form.storeSlug && (
                <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg text-sm"
                  style={{
                    background: "rgba(0,0,0,0.2)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#E0E0E0"
                  }}>

                  <div className="truncate" style={{ overflow: "hidden", direction: "ltr", textAlign: "left" }}>
                    <span className="opacity-70">{isArabic ? "رابط المتجر:" : "Store URL:"}</span><br />
                    <span className="fw-bold" style={{ color: COLORS.gold, direction: "ltr" }}>
                      {`https://${form.storeSlug}.madar.iq/`}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://${form.storeSlug}.madar.iq/`);
                        Swal.fire({
                          icon: "success",
                          title: isArabic ? "تم النسخ" : "Copied",
                          timer: 1500,
                          showConfirmButton: false,
                          background: "#fff",
                          color: "#000",
                          toast: true,
                          position: "top-end"
                        });
                      }}
                      className="p-2 rounded-md hover:scale-105 transition"
                      style={{ background: "rgba(255,255,255,0.1)" }}
                    >
                      <FiLink className="text-white" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-white" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic ? "نوع القطاع" : "Business Sector"}
              </label>
              <input
                type="text"
                name="sector"
                value={form.sector}
                onChange={handleChange}
                className="form-control"
                placeholder={
                  isArabic
                    ? "مثلاً: أزياء، الكترونيات ..."
                    : "e.g. Fashion, Electronics, Restaurants..."
                }
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-white" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic
                  ? "طرق الدفع المتاحة"
                  : "Available Payment Methods"}
              </label>
              <div className="d-flex flex-column gap-2 mt-2">
                {paymentOptions.map((p, i) => (
                  <label
                    key={i}
                    className="d-flex align-items-center gap-2"
                    style={{
                      cursor: "pointer",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      direction: isArabic ? "rtl" : "ltr",
                    }}
                  >
                    <input
                      type="checkbox"
                      value={p.name}
                      checked={form.paymentMethods.includes(p.name)}
                      onChange={handlePaymentChange}
                      style={{
                        width: isMobile ? "16px" : "18px",
                        height: isMobile ? "16px" : "18px",
                        cursor: "pointer",
                        accentColor: COLORS.gold,
                      }}
                    />
                    <span style={{
                      color: COLORS.textLight,
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}>
                      {isArabic ? p.labelAr : p.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-white" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
                {isArabic ? "الخدمات المقدمة" : "Services Provided"}
              </label>
              <textarea
                name="services"
                value={form.services}
                onChange={handleChange}
                className="form-control"
                rows={isMobile ? 3 : 4}
                placeholder={
                  isArabic
                    ? "اذكر الخدمات التي يقدمها متجرك"
                    : "Describe your store's services"
                }
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                  resize: "vertical",
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn fw-semibold py-2 w-100 mt-4"
              style={{
                background: COLORS.gold,
                color: COLORS.darkPurple,
                borderRadius: 10,
                boxShadow: "0 4px 15px rgba(242,180,65,0.4)",
                transition: "0.3s",
                opacity: loading ? 0.7 : 1,
                border: "none",
                fontSize: isMobile ? "0.95rem" : "1rem",
                padding: isMobile ? "10px" : "12px",
              }}
              onMouseOver={(e) => !loading && (e.currentTarget.style.opacity = "0.9")}
              onMouseOut={(e) => !loading && (e.currentTarget.style.opacity = "1")}
            >
              {loading
                ? isArabic
                  ? "جارٍ الحفظ..."
                  : "Saving..."
                : isArabic
                  ? "التالي"
                  : "Next"}
            </button>

            <button
              type="button"
              onClick={handleGoToLogin}
              className="btn fw-semibold py-2 w-100 mt-3"
              style={{
                background: "rgba(255,255,255,0.1)",
                color: COLORS.textLight,
                border: `1.5px solid ${COLORS.gold}`,
                borderRadius: 10,
                transition: "0.3s",
                fontSize: isMobile ? "0.95rem" : "1rem",
                padding: isMobile ? "10px" : "12px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = COLORS.gold;
                e.currentTarget.style.color = COLORS.darkPurple;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                e.currentTarget.style.color = COLORS.textLight;
              }}
            >
              {isArabic ? "العودة لتسجيل الدخول" : "Back to Login"}
            </button>
          </form>
        </div>

      </div>
    </>
  );
}
